import supabase from '../supabase/supabase';

function operationError(message) {
  return new Error(message);
}

const PRODUCT_SELECT = `
  *,
  category:zevi_categories_20260925(id,name),
  media:zevi_product_media_20260925(
    id,media_url,media_type,alt_text,is_primary,sort_order,variant_id
  ),
  variants:zevi_product_variants_20260925(
    id,name,sku,color,color_hex,price_override,active,attributes
  ),
  inventory:zevi_inventory_20260925(
    id,variant_id,quantity_on_hand,quantity_reserved,
    quantity_committed,low_stock_threshold
  )
`;

export async function getAdminProducts() {
  const { data, error } = await supabase
    .from('zevi_products_20260925')
    .select(PRODUCT_SELECT)
    .neq('status', 'archived')
    .is('archived_at', null)
    .order('created_at', { ascending: false });

  if (error) throw operationError('Products could not be loaded.');
  return data || [];
}

export async function getArchivedProducts() {
  const { data, error } = await supabase
    .from('zevi_products_20260925')
    .select(PRODUCT_SELECT)
    .eq('status', 'archived')
    .order('archived_at', { ascending: false });

  if (error) throw operationError('Archived products could not be loaded.');
  return data || [];
}

async function saveProductMedia(productId, productName, media) {
  if (!Array.isArray(media)) return;

  const { error: removeError } = await supabase
    .from('zevi_product_media_20260925')
    .delete()
    .eq('product_id', productId)
    .is('variant_id', null);

  if (removeError) {
    throw operationError('Existing product media could not be updated.');
  }

  if (media.length === 0) return;

  const rows = media.map((item, index) => ({
    product_id: productId,
    variant_id: null,
    media_url: item.media_url.trim(),
    media_type: item.media_type || 'image',
    alt_text: item.alt_text?.trim() || `${productName} saree`,
    is_primary: item.is_primary || index === 0,
    sort_order: index
  }));

  const { error } = await supabase
    .from('zevi_product_media_20260925')
    .insert(rows);

  if (error) throw operationError('Product media could not be saved.');
}

async function saveVariantMedia(productId, productName, color) {
  const variantId = color.variant_id;
  if (!variantId) return;

  const { error: removeError } = await supabase
    .from('zevi_product_media_20260925')
    .delete()
    .eq('product_id', productId)
    .eq('variant_id', variantId);

  if (removeError) {
    throw operationError(
      `Media for ${color.name} could not be updated.`
    );
  }

  const mediaItems = (color.media || [])
    .map((item) => ({
      ...item,
      media_url: String(item.media_url || '').trim(),
      media_type: item.media_type === 'video' ? 'video' : 'image'
    }))
    .filter((item) => item.media_url);

  if (mediaItems.length === 0) return;

  const rows = mediaItems.map((item, index) => ({
    product_id: productId,
    variant_id: variantId,
    media_url: item.media_url,
    media_type: item.media_type,
    alt_text: `${productName} - ${color.name} - ${
      item.media_type === 'video' ? 'video' : 'view'
    } ${index + 1}`,
    is_primary: index === 0,
    sort_order: index
  }));

  const { error } = await supabase
    .from('zevi_product_media_20260925')
    .insert(rows);

  if (error) {
    throw operationError(`Media for ${color.name} could not be saved.`);
  }
}

async function saveProductVariants(
  productId,
  productName,
  colors,
  existingVariants
) {
  const existingMap = new Map(
    (existingVariants || []).map((v) => [v.id, v])
  );

  const keepIds = new Set(
    colors.filter((c) => c.variant_id).map((c) => c.variant_id)
  );

  const toDelete = (existingVariants || [])
    .filter((v) => !keepIds.has(v.id))
    .map((v) => v.id);

  if (toDelete.length > 0) {
    await supabase
      .from('zevi_product_variants_20260925')
      .delete()
      .in('id', toDelete);
  }

  for (const [index, color] of colors.entries()) {
    const sku = `${
      productName.toUpperCase().replace(/[^A-Z0-9]/g, '')
    }-${
      color.name.toUpperCase().replace(/[^A-Z0-9]/g, '')
    }`.slice(0, 40);

    if (color.variant_id && existingMap.has(color.variant_id)) {
      const { error } = await supabase
        .from('zevi_product_variants_20260925')
        .update({
          name: color.name,
          color: color.name,
          color_hex: color.hex || '',
          attributes: {
            color: color.name,
            color_hex: color.hex || '',
            color_order: index
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', color.variant_id);

      if (error) {
        throw operationError(
          `Could not update color variant: ${color.name}`
        );
      }

      const { data: inv } = await supabase
        .from('zevi_inventory_20260925')
        .select('id')
        .eq('variant_id', color.variant_id)
        .maybeSingle();

      if (inv) {
        await supabase.rpc('zevi_admin_adjust_inventory_20260925', {
          input_inventory_id: inv.id,
          input_quantity: Number(color.quantity || 0),
          input_mode: 'set',
          input_reason: 'Color variant stock updated'
        });
      }

      await saveVariantMedia(productId, productName, {
        ...color,
        variant_id: color.variant_id
      });
    } else {
      const { data: newVariant, error } = await supabase
        .from('zevi_product_variants_20260925')
        .insert({
          product_id: productId,
          name: color.name,
          sku: `${sku}-${Date.now()}`,
          color: color.name,
          color_hex: color.hex || '',
          active: true,
          attributes: {
            color: color.name,
            color_hex: color.hex || '',
            color_order: index
          }
        })
        .select()
        .single();

      if (error) {
        throw operationError(
          `Could not create color variant: ${color.name}`
        );
      }

      await supabase.from('zevi_inventory_20260925').insert({
        product_id: productId,
        variant_id: newVariant.id,
        quantity_on_hand: Number(color.quantity || 0),
        quantity_reserved: 0,
        quantity_committed: 0,
        low_stock_threshold: 2
      });

      color.variant_id = newVariant.id;
      await saveVariantMedia(productId, productName, color);
    }
  }
}

async function saveProductInventory(product, values) {
  const inventory =
    product.inventory?.find((i) => !i.variant_id)
    || values.inventory?.find((i) => !i.variant_id);
  const quantity = Math.max(0, Number(values.quantity_on_hand || 0));
  const threshold = Math.max(0, Number(values.low_stock_threshold || 2));

  if (inventory?.id) {
    const { error } = await supabase.rpc(
      'zevi_admin_adjust_inventory_20260925',
      {
        input_inventory_id: inventory.id,
        input_quantity: quantity,
        input_mode: 'set',
        input_reason: 'Quantity updated from product editor'
      }
    );

    if (error) {
      throw operationError(
        'The quantity cannot be lower than reserved or committed stock.'
      );
    }

    const { error: thresholdError } = await supabase
      .from('zevi_inventory_20260925')
      .update({
        low_stock_threshold: threshold,
        updated_at: new Date().toISOString()
      })
      .eq('id', inventory.id);

    if (thresholdError) {
      throw operationError('The low-stock alert level could not be saved.');
    }
    return;
  }

  const { error } = await supabase
    .from('zevi_inventory_20260925')
    .insert({
      product_id: product.id,
      variant_id: null,
      quantity_on_hand: quantity,
      quantity_reserved: 0,
      quantity_committed: 0,
      low_stock_threshold: threshold
    });

  if (error) {
    throw operationError('The product quantity could not be saved.');
  }
}

export async function saveProduct(values) {
  const quantity = Math.max(0, Number(values.quantity_on_hand || 0));
  const threshold = Math.max(0, Number(values.low_stock_threshold || 2));
  const hasColors = values.colors && values.colors.length > 0;

  const payload = {
    name: values.name.trim(),
    slug: values.slug.trim().toLowerCase(),
    description: values.description?.trim() || '',
    price: Number(values.price),
    category_id: values.category_id || null,
    status: values.status,
    availability_status: hasColors
      ? 'in_stock'
      : quantity === 0
        ? 'out_of_stock'
        : quantity <= threshold
          ? 'low_stock'
          : 'in_stock',
    featured: Boolean(values.featured),
    track_inventory: true,
    updated_at: new Date().toISOString()
  };

  let product;

  if (values.id) {
    const result = await supabase
      .from('zevi_products_20260925')
      .update(payload)
      .eq('id', values.id)
      .select()
      .single();

    if (result.error) {
      throw operationError('The product could not be updated.');
    }
    product = {
      ...result.data,
      inventory: values.inventory,
      variants: values.variants
    };
  } else {
    const result = await supabase
      .from('zevi_products_20260925')
      .insert(payload)
      .select()
      .single();

    if (result.error) {
      throw operationError('The product could not be created.');
    }
    product = result.data;
  }

  if (hasColors) {
    await saveProductVariants(
      product.id,
      values.name,
      values.colors,
      values.variants
    );
  }

  await saveProductInventory(product, values);
  await saveProductMedia(product.id, values.name, values.media);

  return product;
}

export async function archiveProduct(id) {
  const { error } = await supabase
    .from('zevi_products_20260925')
    .update({
      status: 'archived',
      archived_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (error) throw operationError('The product could not be archived.');
}

export async function restoreProduct(id) {
  const { error } = await supabase
    .from('zevi_products_20260925')
    .update({
      status: 'active',
      archived_at: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (error) throw operationError('The product could not be restored.');
}

export async function deleteProductPermanently(id) {
  const { data: target, error: lookupError } = await supabase
    .from('zevi_products_20260925')
    .select('id,status')
    .eq('id', id)
    .maybeSingle();

  if (lookupError || !target) {
    throw operationError('The product could not be found.');
  }

  if (target.status !== 'archived') {
    throw operationError(
      'Only archived products can be permanently deleted. Archive it first.'
    );
  }

  // Variants, media, inventory and cart lines are removed by database
  // cascade. Historical order items keep their own product snapshot and
  // simply lose the product link, so past orders stay intact.
  const { data, error } = await supabase
    .from('zevi_products_20260925')
    .delete()
    .eq('id', id)
    .select('id');

  if (error) {
    throw operationError(
      'The product could not be permanently deleted. Related records may still reference it.'
    );
  }

  if (!data || data.length === 0) {
    throw operationError(
      'Nothing was deleted. Your account may not have permission to delete products.'
    );
  }
}

export async function getCategoriesAdmin() {
  const { data, error } = await supabase
    .from('zevi_categories_20260925')
    .select('*')
    .order('sort_order');

  if (error) throw operationError('Categories could not be loaded.');
  return data || [];
}

export async function saveCategory(values) {
  const payload = {
    name: values.name.trim(),
    slug: values.slug.trim().toLowerCase(),
    description: values.description?.trim() || '',
    active: values.active,
    sort_order: Number(values.sort_order || 0),
    updated_at: new Date().toISOString()
  };

  const query = values.id
    ? supabase
      .from('zevi_categories_20260925')
      .update(payload)
      .eq('id', values.id)
    : supabase.from('zevi_categories_20260925').insert(payload);

  const { error } = await query;
  if (error) throw operationError('The category could not be saved.');
}

export async function deleteCategory(id) {
  const { error } = await supabase
    .from('zevi_categories_20260925')
    .delete()
    .eq('id', id);

  if (error) {
    throw operationError(
      'The category could not be deleted. It may have products assigned.'
    );
  }
}

export async function getPromotions() {
  try {
    const { data, error } = await supabase
      .from('zevi_promo_codes_20260925')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      if (
        error.code === '42P01'
        || error.message?.includes('does not exist')
      ) {
        return [];
      }
      throw operationError('Promotions could not be loaded.');
    }
    return data || [];
  } catch {
    return [];
  }
}

export async function savePromotion(values) {
  const payload = {
    code: values.code.trim().toUpperCase(),
    description: values.description?.trim() || '',
    discount_type: values.discount_type,
    discount_value: Number(values.discount_value),
    minimum_subtotal: values.minimum_subtotal
      ? Number(values.minimum_subtotal)
      : null,
    usage_limit: values.usage_limit ? Number(values.usage_limit) : null,
    starts_at: values.starts_at || null,
    ends_at: values.ends_at || null,
    active: values.active,
    updated_at: new Date().toISOString()
  };

  const { error } = await supabase
    .from('zevi_promo_codes_20260925')
    .upsert(payload, { onConflict: 'code' });

  if (error) throw operationError('The promotion could not be saved.');
}

export async function getShipping() {
  const { data, error } = await supabase
    .from('zevi_shipping_methods_20260925')
    .select('*,rates:zevi_shipping_rates_20260925(*)')
    .order('sort_order');

  if (error) {
    throw operationError('Shipping methods could not be loaded.');
  }

  if (!data || data.length === 0) {
    const { data: newMethod, error: createError } = await supabase
      .from('zevi_shipping_methods_20260925')
      .insert({
        name: 'Standard Delivery',
        description: 'Home delivery across Sri Lanka',
        active: true,
        sort_order: 1
      })
      .select()
      .single();

    if (createError) {
      throw operationError('Could not create default shipping method.');
    }
    return [{ ...newMethod, rates: [] }];
  }

  return data || [];
}

export async function saveShippingDistrict(values) {
  const methods = await getShipping();
  const methodId = values.shipping_method_id || methods[0]?.id;

  if (!methodId) {
    throw operationError('No shipping method available.');
  }

  const { error } = await supabase.rpc(
    'zevi_admin_save_shipping_district_20260925',
    {
      input_rate_id: values.id || null,
      input_shipping_method_id: methodId,
      input_district: values.district,
      input_rate: Number(values.rate),
      input_active: values.active !== false
    }
  );

  if (error) {
    throw operationError('The delivery district could not be saved.');
  }
}

export async function getStorePickupSettings() {
  const { data, error } = await supabase
    .from('zevi_site_settings_20260925')
    .select('setting_key,setting_value')
    .in('setting_key', [
      'store_pickup_address',
      'store_pickup_enabled'
    ]);

  if (error) {
    throw operationError('Store pickup settings could not be loaded.');
  }

  const settings = {
    enabled: false,
    address: '',
    city: '',
    phone: '',
    hours: ''
  };

  for (const row of data || []) {
    if (row.setting_key === 'store_pickup_enabled') {
      settings.enabled = row.setting_value?.enabled || false;
    } else if (row.setting_key === 'store_pickup_address') {
      settings.address = row.setting_value?.address || '';
      settings.city = row.setting_value?.city || '';
      settings.phone = row.setting_value?.phone || '';
      settings.hours = row.setting_value?.hours || '';
    }
  }

  return settings;
}

export async function saveStorePickupSettings(settings) {
  const updates = [
    {
      setting_key: 'store_pickup_enabled',
      setting_group: 'delivery',
      setting_value: { enabled: Boolean(settings.enabled) },
      visibility: 'public',
      updated_at: new Date().toISOString()
    },
    {
      setting_key: 'store_pickup_address',
      setting_group: 'delivery',
      setting_value: {
        address: settings.address?.trim() || '',
        city: settings.city?.trim() || '',
        phone: settings.phone?.trim() || '',
        hours: settings.hours?.trim() || ''
      },
      visibility: 'public',
      updated_at: new Date().toISOString()
    }
  ];

  const { error } = await supabase
    .from('zevi_site_settings_20260925')
    .upsert(updates, { onConflict: 'setting_key' });

  if (error) {
    throw operationError('Store pickup settings could not be saved.');
  }
}

export async function getCustomers() {
  try {
    const { data, error } = await supabase
      .from('zevi_customers_20260925')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      if (
        error.code === '42P01'
        || error.message?.includes('does not exist')
      ) {
        return [];
      }
      throw operationError('Customers could not be loaded.');
    }
    return data || [];
  } catch {
    return [];
  }
}

export async function getPaymentSettings() {
  const { data, error } = await supabase
    .from('zevi_payment_settings_20260925')
    .select('*')
    .order('method');

  if (error) {
    throw operationError('Payment settings could not be loaded.');
  }
  return data || [];
}

export async function savePaymentSetting(setting) {
  const qrUrl = String(setting.qr_image_url || '').trim();
  const instructions = String(
    setting.customer_instructions || ''
  ).trim();

  if (
    setting.method === 'bank_transfer'
    && setting.enabled
    && !instructions
  ) {
    throw operationError(
      'Add the bank account details before enabling Bank Transfer.'
    );
  }

  if (
    setting.method === 'qr_payment'
    && setting.enabled
    && !qrUrl
  ) {
    throw operationError(
      'Add the QR code image URL before enabling QR Payment.'
    );
  }

  const { error } = await supabase
    .from('zevi_payment_settings_20260925')
    .update({
      enabled: Boolean(setting.enabled),
      customer_instructions: instructions,
      qr_image_url: qrUrl || null,
      updated_at: new Date().toISOString()
    })
    .eq('id', setting.id);

  if (error) {
    throw operationError('Payment settings could not be saved.');
  }
}